import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components/macro';
import { modifiersType } from 'types';
import { applyStyleModifiers } from 'styled-components-modifiers';
import { ScrollToTopOnMount } from 'containers'
import { useParams, Link } from 'react-router-dom';
import { useUserContext } from 'context/UserContext';
import { Title, Text } from 'components/atoms';

const MODIFIERS = {
  fullscreen: () => `
    margin-left: 0;
  `,
  stretch: () => `
    padding: 0;
  `,
  darker: ({ theme }) => `
    background-color: ${theme.colors.bgLighter};
  `,
  relative: () => `
    position: relative;
  `,
  center: () => `
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  storybook: () => `
    background-color: transparent;
    min-width: initial;
  `,
};

const Layout = ({
  children,
  modifiers,
  scrollToTopOnMount,
  header,
  subtitle,
  hasSubwebHeader,
}) => {
  const { subweb } = useParams();
  const [{ user }] = useUserContext();

  const subwebData = React.useMemo(() => {
    if (!user || !user.subwebs || !user.subwebs.length > 0) return null
    const data = user.subwebs.find(
      subwebData => subwebData.slug === subweb || encodeURIComponent(subwebData.slug) === subweb
    )
    return data
  }, [subweb, user])

  return (
    <>
      {scrollToTopOnMount && <ScrollToTopOnMount />}
      <Main modifiers={modifiers}>
        {(header || hasSubwebHeader) && (
          <Layout.Header>
            <Layout.Title>
              {header ? (
                <Title>{header}</Title>
              ) : subwebData && subwebData.namePt && (
                <Link to={`/${subweb}`}>
                  <Title>{subwebData.namePt}</Title>
                </Link>
              )}
              {subtitle && <Text modifiers={['light']}>{subtitle}</Text>}
            </Layout.Title>
          </Layout.Header>
        )}
        {children}
      </Main>
    </>
  )
}

const Main = styled.main`
  background-color: ${props => props.theme.components.layout.backgroundColor};
  padding: ${props => `
  ${props.theme.components.layout.paddingVertical}
  ${' '}
  ${props.theme.components.layout.paddingHorizontal}
  `};
  width: 100%;
  min-width: 875px;
  min-height: 100vh;
  margin-left: ${props => (props.marginLeft ? props.marginLeft : 0)};
  ${applyStyleModifiers(MODIFIERS)};
`;

Layout.Header = styled.header`
  margin-bottom: ${props => props.theme.spacing.l};
  padding-bottom: ${props => props.theme.spacing.xl4};
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  a {
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`

Layout.Title = styled.div`
  display: inline-flex;
  width: 100%;
  flex-direction: column;
`

Layout.Actions = styled.div`
  display: flex;
  height: fit-content;
  width: 100%;
  justify-content: flex-end;
  a {
    text-decoration: none !important;
    font-size: ${props => props.theme.sizes.m} !important;
  }
`

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  modifiers: modifiersType,
  scrollToTopOnMount: PropTypes.bool,
  hasSubwebHeader: PropTypes.bool,
  subtitle: PropTypes.string,
  header: PropTypes.string,
};

Layout.defaultProps = {
  modifiers: '',
  scrollToTopOnMount: true,
  hasSubwebHeader: false,
  subtitle: undefined,
  header: undefined,
};

export default withTheme(Layout);
